/**
 * SocketContext – React context providing a shared Socket.IO connection.
 *
 * Usage:
 *   import { useSocket } from '../contexts/SocketContext';
 *   const socket = useSocket();
 *   useEffect(() => { socket?.on('server:updated', handler); return () => socket?.off(…); }, [socket]);
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

// Exposed so any component can read the live transport without re-renders
export const socketTransportRef = { current: 'disconnected' };

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [transport, setTransport] = useState('disconnected');

  useEffect(() => {
    // In Docker dev we connect directly to API host; in prod it can stay same-origin via proxy.
    const apiRoot = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? window.location.origin).replace(/\/$/, '');
    const socketBase = apiRoot || window.location.origin;

    // Sin sesión no hay nada que escuchar. Importa desde que existe la página
    // pública `/instalar-agente`: antes se abría un socket anónimo que el
    // backend rechaza y el cliente reintentaba cada 2 s indefinidamente.
    if (!localStorage.getItem('token')) {
      return undefined;
    }

    const s = io(socketBase, {
      path: '/socket.io/',
      // El backend usa el token para unir la consola a la room de su empresa y
      // así recibir la presencia de sus agentes sin mezclar inquilinos. Con
      // función, cada (re)conexión envía el token más fresco del localStorage.
      auth: (cb) => cb({ token: localStorage.getItem('token') || '' }),
      transports: ['polling', 'websocket'],
      upgrade: true,
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: Infinity,
      timeout: 10000,
    });

    s.on('connect', () => {
      const t = s.io.engine.transport.name;
      socketTransportRef.current = t;
      setTransport(t);
      console.log(`[WS] connected  url=${socketBase}  sid=${s.id}  transport=${t}`);
    });

    // Engine.IO upgrade events (polling → websocket)
    s.io.engine.on('upgrade', (newTransport) => {
      socketTransportRef.current = newTransport.name;
      setTransport(newTransport.name);
      console.log(`[WS] ✅ UPGRADED to ${newTransport.name}`);
    });

    s.io.engine.on('upgradeError', (err) => {
      console.error('[WS] ❌ upgrade FAILED:', err.message || err);
    });

    s.on('disconnect', (reason) => {
      socketTransportRef.current = 'disconnected';
      setTransport('disconnected');
      console.log('[WS] disconnected', reason);
    });

    s.on('connect_error', (err) => console.error('[WS] connect_error', err.message));

    setSocket(s);

    return () => {
      s.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, transport }}>
      {children}
    </SocketContext.Provider>
  );
}

/** Returns the Socket.IO socket instance (backward-compatible). */
export function useSocket() {
  return useContext(SocketContext)?.socket ?? null;
}

/** Returns the current Engine.IO transport: 'polling' | 'websocket' | 'disconnected'. */
export function useSocketTransport() {
  return useContext(SocketContext)?.transport ?? 'disconnected';
}

export default SocketContext;
